// F16 — Aqana landing design system, "One line".
//
// The direction is taken from the logo rather than invented: the mark is a
// single unbroken stroke in black ink on warm bone paper. That is also the
// product's pitch — one system, counter to books, nothing typed twice.
//
// There is deliberately NO accent colour. Colour enters a landing page only
// through product screenshots, which makes the app the most colourful thing
// on any screen and sidesteps the blue-vs-green split between the POS app
// and the Manager portal without repainting either.
//
// This file is the ONLY place in the landing suite that may contain a hex
// literal. Everything else imports from here.

export const C = {
    paper: '#f4efe7',       // page ground, the default surface
    paperDeep: '#ebe3d7',   // alternating sections, quiet separation
    ink: '#1b2230',         // headings, body, the mark
    inkMuted: '#6b6257',    // secondary text
    inkFaint: '#8a8073',    // captions, labels, disabled
    surface: '#ffffff',     // cards and device screens — things sitting ON the paper
    line: '#e3dacd',        // borders, rules
    lineStrong: '#d9cfc0',  // input borders, secondary button outline

    // Form validation only. Never decorative, never a brand colour.
    success: '#2f6f4f',
    warning: '#8a6a1f',
    danger: '#9b3226',
};

// Translucent ink/paper washes. Kept here so no page needs an rgba() literal.
export const A = {
    inkWash: 'rgba(27, 34, 48, 0.06)',
    inkRing: 'rgba(27, 34, 48, 0.12)',
    inkShadow: 'rgba(27, 34, 48, 0.10)',
    inkShadowSoft: 'rgba(27, 34, 48, 0.06)',
    paperMuted: 'rgba(244, 239, 231, 0.72)',
    paperFaint: 'rgba(244, 239, 231, 0.62)',
    paperLine: 'rgba(244, 239, 231, 0.16)',
};

export const FONT = {
    display: "'Outfit', system-ui, -apple-system, sans-serif",
    body: "'IBM Plex Sans', system-ui, -apple-system, sans-serif",
    // Arabic comes from the same superfamily as the body face, so weight and
    // warmth match across scripts without tuning a cross-family pairing.
    arabic: "'IBM Plex Sans Arabic', 'Cairo', system-ui, sans-serif",
};

export const TYPE = {
    displayXl: { fontFamily: FONT.display, fontSize: 'clamp(2.5rem, 6vw, 4.25rem)', lineHeight: 1.04, letterSpacing: '-0.03em', fontWeight: 700 },
    displayL: { fontFamily: FONT.display, fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.08, letterSpacing: '-0.022em', fontWeight: 700 },
    displayM: { fontFamily: FONT.display, fontSize: 'clamp(1.5rem, 3vw, 2rem)', lineHeight: 1.15, letterSpacing: '-0.015em', fontWeight: 700 },
    title: { fontFamily: FONT.display, fontSize: '1.25rem', lineHeight: 1.3, letterSpacing: '-0.01em', fontWeight: 500 },
    bodyL: { fontFamily: FONT.body, fontSize: '1.0625rem', lineHeight: 1.65, fontWeight: 400 },
    body: { fontFamily: FONT.body, fontSize: '0.9375rem', lineHeight: 1.7, fontWeight: 400 },
    caption: { fontFamily: FONT.body, fontSize: '0.8125rem', lineHeight: 1.6, fontWeight: 400 },
    label: { fontFamily: FONT.body, fontSize: '0.6875rem', lineHeight: 1, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' },
};

export const SP = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, '2xl': 32, '3xl': 48, '4xl': 64, '5xl': 96, '6xl': 128 };

export const R = { sm: 6, md: 10, lg: 16, pill: 999 };

export const W = { content: 1120, prose: 720 };

// ---- Shared style recipes -------------------------------------------------
// Logical properties throughout: nothing here may depend on left-to-right
// reading order, because the site ships in Arabic at launch.

export const S = {
    // section({ deep, tight }) — a full-bleed band. `deep` alternates the ground.
    section: ({ deep = false, tight = false } = {}) => ({
        background: deep ? C.paperDeep : C.paper,
        paddingBlock: tight ? SP['3xl'] : SP['5xl'],
        paddingInline: SP.xl,
    }),

    container: {
        maxWidth: W.content,
        marginInline: 'auto',
        width: '100%',
    },

    prose: {
        maxWidth: W.prose,
        ...TYPE.bodyL,
        color: C.inkMuted,
    },

    btnPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: SP.sm,
        background: C.ink,
        color: C.paper,
        border: `1px solid ${C.ink}`,
        borderRadius: R.md,
        paddingBlock: 13,
        paddingInline: SP.xl,
        fontFamily: FONT.body,
        fontSize: '0.9375rem',
        fontWeight: 600,
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'opacity .18s ease',
    },

    btnSecondary: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: SP.sm,
        background: 'transparent',
        color: C.ink,
        border: `1px solid ${C.lineStrong}`,
        borderRadius: R.md,
        paddingBlock: 13,
        paddingInline: SP.xl,
        fontFamily: FONT.body,
        fontSize: '0.9375rem',
        fontWeight: 600,
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'border-color .18s ease',
    },

    card: {
        background: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: R.lg,
        padding: SP['2xl'],
    },

    label: {
        ...TYPE.label,
        color: C.inkFaint,
        display: 'block',
        marginBlockEnd: SP.md,
    },
};

const tokens = { C, A, FONT, TYPE, SP, R, W, S };
export default tokens;
